import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import useFetchData from "../hooks/useFetchData";
import { getSvgMap } from "../utils/dashboardUtils";

import LegendMetricModal from "../components/LegendMetricModal";
import MetricsModal from "./MetricsModal";

import MetricCategoriesContext from "../context/MetricCategoriesContext";
import { handleExcelDownload } from "../utils/excelExportModule";
import DownloadLoading from "../components/DownloadLoading";
import MetricCard from "../components/MetricCard";
import downloadSvg from '../assets/download.svg';
import printSvg from '../assets/print.svg';
import mapSvg from '../assets/map.svg';
import layerSvg from '../assets/layer.svg';
import MappableMetricsModal from "../components/mappableModal/MappableMetricsModal";
import LayersModal from "../components/layers/LayersModal";
import LayersContext from "../context/LayersContext";
import LayerPanel from "../components/layers/LayerPanel";
import MetadataContext from "../context/MetadataContext";
import ClearMapSelectionModal from "../components/ClearMapSelectionModal";
import { useOrgContext } from "../context/OrgIdContext";
import MapItemDetailsModal from "../components/MapItemDetailsModal";
import ProgrammaticModal from "./ProgrammaticModal";
import TabMenu from "../components/TabMenu";
import TabMenuSkeleton from "../components/skeletons/TabMenuSkeleton";
import MetricCardSkeleton from "../components/skeletons/MetricCardSkeleton";
import TableSkeleton from "../components/skeletons/TableSkeleton";
import RaceEthnicityBarChartCard from "../components/charts/RaceEthnicityBarChartCard";
import { DashboardColorContext } from "../context/DashboardColorContext";
import { getEducationMetricOrder, tabColors } from "../utils/helpers";
import MetricBarChart from "../components/charts/MetricBarChart";
import IndexBanner from "../components/IndexBanner";
import DisplayMetricCard from "../components/mdd/DisplayMetricCard";
import schoolIcon from "../assets/general/school.svg";
import DoubleMetricCard from "../components/DoubleMetricCard";
import TripleMetricSection from "../components/TripleMetricSection";

const Dashboard = () => {
    const isIframe = window.self !== window.top;
    const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const { orgId, locationId } = useOrgContext();
    const { layers } = useContext(LayersContext);
    const { metricCategories, loading: categoriesLoading } = useContext(MetricCategoriesContext);
    // Fetch metric metadata for the current org
    const { metadata, loading: metadataLoading } = useContext(MetadataContext);
    const prevViewRef = useRef<string | null>(null);

    // Message prop state
    const [currentTab, setCurrentTab] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [legendUrl, setLegendUrl] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [siteUrl, setSiteUrl] = useState('');
    const [legendId, setLegendId] = useState(0);

    // Modal state
    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const [isMappableModalOpen, setIsMappableModalOpen] = useState(false);
    const [isLayersModalOpen, setIsLayersModalOpen] = useState(false);
    const [isProgrammaticModalOpen, setIsProgrammaticModalOpen] = useState(false);

    // Excel state
    const [shouldDownloadExcel, setShouldDownloadExcel] = useState(false);
    const [showDownloadLoading, setShowDownloadLoading] = useState(false);

    // Map item details modal state
    const [mapItemDetailModalOpen, setMapItemDetailModalOpen] = useState(false);
    const [mapItemParams, setMapItemParams] = useState({ layerId: null, id: null });    

    // Geographic levels and exclusion lists defined for excel (will vary by project)
    const geographicLevels = ['tract', 'block group'];
    const summaryExclusionList: string[] = [];
    const disaggregationExclusionList: string[] = [];
    const geographyExclusionList: string[] = [];

    const { indicators, loading, dashboardView, geoDetails, geoTag, selectionActive, lodMissing } = useFetchData(currentTab || '');

    const tabs = metricCategories;

    // "Ready" flag and tracking metric tabs
    const isLoading = (!currentTab && layers.length === 0) ||
    categoriesLoading ||
    metadataLoading ||
    loading;

    const isMetricTab = useMemo(() => {
        if (categoriesLoading || !metricCategories.length || !currentTab) return undefined;
        
        return (
            !!currentTab &&
            metricCategories.length > 0 &&
            metricCategories.map(c => c.toLowerCase()).includes(currentTab.toLowerCase())
        );
    }, [metricCategories, currentTab]);

    // #region React useEffects
    // useEffect(() => {
    //     if (locationId === "federalway" && location.pathname !== "/parcels") {
    //         navigate("/parcels", { replace: true });
    //     }
    // }, [locationId, location.pathname, navigate]);


    useEffect(() => {
        if (tabs.length === 0) return;

        if (!isIframe) {
            if (tab && tabs.includes(tab)) {
                setCurrentTab(tab.toLowerCase());
            } else if (!currentTab) {
                setCurrentTab(tabs[0]);
            }
        }
    }, [tab, isIframe, tabs]);

    useEffect(() => {
        if (tabs.length === 0) return;

        if (isIframe && dashboardView) {
            // Check if dashboardView matches a layer_name and add it to tabs
            const foundLayer = layers.find((layer) => layer.layer_name.toLowerCase() === dashboardView.toLowerCase());

            // If foundLayer is present, update the tabs
            if (foundLayer && !tabs.includes(dashboardView)) {
                const updatedTabs = [...tabs, dashboardView];
                setCurrentTab(updatedTabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || updatedTabs[0]);
            } else {
                setCurrentTab(tabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || tabs[0]);
            }
        } else {
            // When not in iframe, just ensure the currentTab is set based on the `tab` param
            setCurrentTab(tabs.find((t) => t.toLowerCase() === currentTab?.toLowerCase()) || tabs[0]);
        }
    }, [dashboardView, isIframe, tabs, layers]);

    // Combine data from all sources
    const allDataAvailable = useMemo(() => {
        return !loading && indicators;
    }, [loading, indicators]);


    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data.message === 'pdf') {
                setPdfUrl(event.data.params.url);
                setLogoUrl(event.data.params.logoUrl || '');
                setLegendUrl(event.data.params.legendUrl || '');
                setSelectedArea(event.data.params.selectedArea || '');
                setSiteUrl(event.data.params.siteUrl || '');
                setIsModalOpen(true);

                // Send message to expand
                window.parent.postMessage({ 
                    message: "expand",
                    mode: "fullscreen"
                }, '*');
                console.log('expand message sent');
            } else if (event.data.message === 'metric-modal') {

                // Legend metadata info icon event
                console.log('Message received from parent app inside legend metric modal:', event.data);
                setLegendId(event.data.params.id);
            } else if (event.data.message === 'excel') {
                // Get selected area
                const sArea = event.data?.params?.selectedArea;
                // Show loading modal
                setShowDownloadLoading(true);

                if (!loading) {
                    // Execute excel download script
                    handleExcelDownload(orgId, locationId, geoDetails, geoTag, metricCategories, sArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                        setShowDownloadLoading(false);
                    });
                } else {
                    console.log('Data is still loading, will attempt to download Excel later.');
                    setShouldDownloadExcel(true); // Set flag to retry download
                }
            } else if (event.data.message === 'pointLayerDetails') {
                console.log('point layer message:', event.data);
                const { layerId, id } = event.data.params || {};
                if (layerId && id) {
                    setMapItemParams({ layerId, id });
                    setMapItemDetailModalOpen(true);
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [allDataAvailable, geoDetails]);

    useEffect(() => {
        if (!loading && shouldDownloadExcel) {
            setShowDownloadLoading(true);
            handleExcelDownload(orgId, locationId, geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShouldDownloadExcel(false); 
                setShowDownloadLoading(false);
            });
        }
    }, [loading, shouldDownloadExcel, geoDetails]);

    // Close map item detail modal when dashboard panel changes
    useEffect(() => {
        if (prevViewRef.current !== null && prevViewRef.current !== dashboardView) {
            setMapItemDetailModalOpen(false);
        }
        // Update dashboard view ref
        prevViewRef.current = dashboardView;
    }, [dashboardView]);


    // EXTRACT SCHOOL COUNTS FOR MDD ONLY
    interface SchoolDetails {
        public?: number;
        private?: number;
    }

    const schoolDetails = useMemo(() => {
        const schoolGeoDetail = geoDetails.find(
            (detail) =>
                detail.lod?.trim().toLowerCase() === "school"
        );

        if (!schoolGeoDetail) {
            return null;
        }

        const details = (
            schoolGeoDetail as typeof schoolGeoDetail & {
                details?: SchoolDetails;
            }
        ).details;

        if (!details) {
            return null;
        }

        return {
            public: Number(details.public ?? 0),
            private: Number(details.private ?? 0),
        };
    }, [geoDetails]);
    //#endregion

    //#region Event Handlers
    const closeLegendModal = () => {
        setIsLegendModalOpen(false);
        setLegendId(-1);
    };

    const closeModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        let origin = '*';

        e.preventDefault();
        setIsModalOpen(false);
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, origin);
        console.log('expand message sent');
    };

    const handleDownloadNoIframe = () => {
        // Show loading modal
        setShowDownloadLoading(true);

        if (!loading) {
            // Execute excel download script
            handleExcelDownload(orgId, locationId, geoDetails, geoTag, metricCategories, selectedArea, summaryExclusionList, disaggregationExclusionList, geographyExclusionList, geographicLevels, 'Base Download').finally(() => {
                setShowDownloadLoading(false);
            });
        }
    };

    const handlePrintNoIframe = () => {
        setPdfUrl('');
        setLogoUrl('');
        setLegendUrl('');
        setSelectedArea('');
        setSiteUrl('');
        setIsModalOpen(true);
    };

    const handleShowLayers = () => {
        setIsLayersModalOpen((prevState) => !prevState);
    };

    const handleMappableModal = () => {
        setIsMappableModalOpen(true);
    }

    const closeMappableModal = () => {
        setIsMappableModalOpen(false);
    }

    const closeMapItemModal = () => setMapItemDetailModalOpen(false);

    // Programmatic PDF Modal Handlers
    const openProgrammaticModal = () => {
        setIsProgrammaticModalOpen(true);
        window.parent.postMessage(
            {
            message: "expand",
            mode: "fullscreen",
            },
            "*"
        );
        console.log('expand message sent');
    };

    const closeProgrammaticModal = () => {
        setIsProgrammaticModalOpen(false);
        let origin = '*';
        // Send message to expand
        window.parent.postMessage({ message: "expand" }, origin);
        console.log('expand message sent');
    };
    //#endregion

    const renderMetrics = (tab: string) => {
        if (!metricCategories || metricCategories.length === 0) {
            if (layers.length > 0) {
                return (
                    <div className="flex-grow overflow-y-auto px-4">
                        <LayerPanel
                            sharedView={true}
                            dashboardView={tab}
                            onPrintClick={openProgrammaticModal}
                        />
                    </div>
                );
            }

            return null;
        }

        // Show modal to clear map selection if the current active selection is missing data or invalid (Small area problem)
        if (lodMissing) {
            return <ClearMapSelectionModal/>
        }

        // Render Layer Panel if dashboardview is not a metric category
        const isMetricTab = metricCategories.map(c => c.toLowerCase()).includes(tab.toLowerCase());

        if (!isMetricTab) {
            return (
                <div className="flex-grow overflow-y-auto px-4">
                    <LayerPanel
                        sharedView={true}
                        dashboardView={tab}
                        onPrintClick={openProgrammaticModal}
                    />
                </div>
            );
        }

        let metricsForTab = indicators; // Get metrics for the current tab

        // Find the indexMetric from metadata based on the current tab
        const indexMetricId = metadata?.find((metric) => metric.metric_source_type === "index" && metric.metric_category?.toLowerCase() === tab.toLowerCase())?.id;

        // Find the full indicator object that matches indexMetric
        const indexMetricData = indicators.find(metric => metric.id === Number(indexMetricId));

        console.log('index metrics:', indexMetricData);

        // Exclude the index metric from the list of metrics
        if (indexMetricId) {
            metricsForTab = metricsForTab.filter((metric) => metric.id !== indexMetricId);
        }

        const noMetrics =
        !isLoading &&
        isMetricTab &&
        indicators.length > 0 &&
        metricsForTab.length === 0;

        // helper to check for Race & Ethnicity metric
        const isRaceEthnicityMetric = (m: any) =>
            m.name === "Race & Ethnicity" ||
            m.name === "Race and Ethnicity";

        
        // helper to check for population metrics
        const isPopulationMetric = (m: any) =>
            m.name?.toLowerCase().includes("population");

        // helper to check for commute mode metric
        const isCommuteModeMetric = (m: any) =>
            m.name === "Commute Mode";

        // helper to check for annual hh income metric
        const isAnnualHHIncomeMetric = (m: any) =>
            m.name === "Annual Household Income";

        // Helper to check for Families under 200% Poverty metric
        const isFamiliesPovertyMetric = (m: any) =>
            m.name === "Families under 200% Poverty";

        console.log('metrics for tab:', metricsForTab);

        const isEducationTab = tab.trim().toLowerCase() === "education";
        const isEconomyTab = tab.trim().toLowerCase() === "economy";

        // Reorder for education metrics and so that population is first always
        const orderedMetrics = [...metricsForTab].sort(
            (a, b) => {
                if (isEducationTab) {
                    const aEducationOrder =
                        getEducationMetricOrder(a.name);

                    const bEducationOrder =
                        getEducationMetricOrder(b.name);

                    if (aEducationOrder !== bEducationOrder) {
                        return (
                            aEducationOrder -
                            bEducationOrder
                        );
                    }
                }

                // Economy: Families under 200% Poverty always comes last
                if (isEconomyTab) {
                    const aPoverty = isFamiliesPovertyMetric(a);
                    const bPoverty = isFamiliesPovertyMetric(b);

                    if (aPoverty !== bPoverty) {
                        return aPoverty ? 1 : -1;
                    }
                }

                // Population sort behavior
                const aPop = isPopulationMetric(a);
                const bPop = isPopulationMetric(b);

                if (aPop === bPop) {
                    return 0;
                }

                return aPop ? -1 : 1;
            }
        );

        // find the special metric(s)
        const race = orderedMetrics.find(isRaceEthnicityMetric);
        const commute = orderedMetrics.find(isCommuteModeMetric);
        const annualHHIncome = orderedMetrics.find(isAnnualHHIncomeMetric);
        // Find the cost-burden metrics
        const renterCostBurden = orderedMetrics.find(
            (m) => m.name === "Cost-Burdened Renters"
        );

        const ownerCostBurden = orderedMetrics.find(
            (m) => m.name === "Cost-Burdened Homeowners"
        );

        const householdCostBurden = orderedMetrics.find(
            (m) => m.name === "Cost-Burdened Households"
        );

        // Only use the DoubleMetricCard when all three metrics are available
        const hasCostBurdenMetrics = !!(
            renterCostBurden &&
            ownerCostBurden &&
            householdCostBurden
        );

        // Find the degree metrics
        const ged = orderedMetrics.find(
            (m) => m.name === "GED or Equivalent or More"
        );

        const bachelors = orderedMetrics.find(
            (m) => m.name === "Bachelor’s Degree or More"
        );

        const masters = orderedMetrics.find(
            (m) => m.name === "Master's Degree or More"
        );

        // Only use the TripleMetricSection when all three education metrics are available
        const hasGraduationMetrics = !!(
            ged &&
            bachelors &&
            masters
        );

        // Find the housing unit metrics
        const housingUnits = orderedMetrics.find(
            (m) => m.name === "Housing Units"
        );

        const multifamilyUnits = orderedMetrics.find(
            (m) => m.name === "Multifamily Units"
        );

        const rentalUnits = orderedMetrics.find(
            (m) => m.name === "Rental Units"
        );

        // Only use the TripleMetricSection when all three housing metrics are available
        const hasHousingUnitMetrics = !!(
            housingUnits &&
            multifamilyUnits &&
            rentalUnits
        );

        // If you also have a separate Ethnicity metric, grab it here.
        const ethnicity = indicators.find(
            (m: any) => m.metric?.name === "Ethnicity"
        );

        // remove the chart metrics from the MetricCards list
        const nonChartMetrics = orderedMetrics.filter((m) => {
            // Exclude metrics rendered as charts
            if (
                isRaceEthnicityMetric(m) ||
                isCommuteModeMetric(m) ||
                isAnnualHHIncomeMetric(m)
            ) {
                return false;
            }

            // Exclude cost-burden metrics when using DoubleMetricCard
            if (
                hasCostBurdenMetrics &&
                (
                    m.name === "Cost-Burdened Renters" ||
                    m.name === "Cost-Burdened Homeowners" ||
                    m.name === "Cost-Burdened Households"
                )
            ) {
                return false;
            }

            // Exclude educational attainment metrics when using TripleMetricSection
            if (
                hasGraduationMetrics &&
                (
                    m.name === "GED or Equivalent or More" ||
                    m.name === "Bachelor’s Degree or More" ||
                    m.name === "Master's Degree or More"
                )
            ) {
                return false;
            }

            // Exclude housing unit metrics when using TripleMetricSection
            if (
                hasHousingUnitMetrics && (locationId === 'shelbytn' || locationId === 'jeffco') &&
                (
                    m.name === "Housing Units" ||
                    m.name === "Multifamily Units" ||
                    m.name === "Rental Units"
                )
            ) {
                return false;
            }

            return true;
        });

        // Condition for school cards (MDD Specific)
        const shouldShowSchoolCards =
            locationId?.trim().toLowerCase() === "mdd" &&
            tab.trim().toLowerCase() === "education" &&
            schoolDetails !== null;

        const color = tabColors[tab] || "#000";

        return (
            <div className="bg-[#f8f8fa]">
                {indexMetricData && (
                    <div className="mb-4">
                        <IndexBanner
                            key={indexMetricId}
                            metric={indexMetricData}
                            svg={undefined}
                            disaggData={indicators}
                            geoDetails={geoDetails}
                            geoTag={geoTag}
                            selectionActive={selectionActive}
                        />
                    </div>
                )}

                <div>
                    {noMetrics ? (
                        <div className="flex items-center justify-center text-sm text-color-cai-dashboard">
                            No metrics available for {tab}.
                        </div>
                    ) : (
                        <DashboardColorContext.Provider value={color}>
                            <div className="mx-auto px-4 pb-8 pt-4 h-screen">
                                <div className="grid grid-cols-2 gap-4">
                                    {shouldShowSchoolCards && (
                                        <>
                                            <DisplayMetricCard
                                                value={schoolDetails.public}
                                                metricName="Public Schools"
                                                unitDescriptor="# of public schools in the selected area"
                                                icon={schoolIcon}
                                            />

                                            <DisplayMetricCard
                                                value={schoolDetails.private}
                                                metricName="Private Schools"
                                                unitDescriptor="# of private schools in the selected area"
                                                icon={schoolIcon}
                                            />
                                        </>
                                    )}

                                    {/* Housing Cost Burden */}
                                    {hasCostBurdenMetrics &&
                                        renterCostBurden &&
                                        ownerCostBurden &&
                                        householdCostBurden && (
                                            <div className="col-span-2 w-full">
                                                <DoubleMetricCard
                                                    key={householdCostBurden.id}
                                                    metricOne={renterCostBurden}
                                                    metricTwo={ownerCostBurden}
                                                    subtextOne="Renters"
                                                    subtextTwo="Owners"
                                                    title="Housing Cost Burden"
                                                    metadataId={householdCostBurden.id}
                                                    width="w-full"
                                                    disaggData={indicators}
                                                    geoDetails={geoDetails}
                                                    geoTag={geoTag}
                                                    selectionActive={selectionActive}
                                                    showMetadataIcon={true}
                                                />
                                            </div>
                                        )}

                                    {ged &&
                                        bachelors &&
                                        masters && (
                                            <div className="col-span-2 w-full">
                                                <TripleMetricSection
                                                    title="Educational Attainment"
                                                    metricOne={ged}
                                                    metricTwo={bachelors}
                                                    metricThree={masters}
                                                    disaggData={indicators}
                                                    geoDetails={geoDetails}
                                                    geoTag={geoTag}
                                                    selectionActive={selectionActive}
                                                    unitDescriptor={ged.versions[0].unit_descriptor}
                                                    showMetadataIcon={true}
                                                />
                                            </div>
                                        )}

                                    {housingUnits &&
                                        multifamilyUnits &&
                                        rentalUnits && (
                                            <div className="col-span-2 w-full">
                                                <TripleMetricSection
                                                    title="Housing Inventory"
                                                    metricOne={housingUnits}
                                                    metricTwo={multifamilyUnits}
                                                    metricThree={rentalUnits}
                                                    disaggData={indicators}
                                                    geoDetails={geoDetails}
                                                    geoTag={geoTag}
                                                    selectionActive={selectionActive}
                                                    unitDescriptor={housingUnits.versions[0].unit_descriptor}
                                                    showMetadataIcon={true}
                                                />
                                            </div>
                                        )}

                                    {nonChartMetrics.map(metric => (
                                        <MetricCard
                                            key={metric.id}
                                            metric={metric}
                                            width="w-full"
                                            svg={undefined}
                                            disaggData={indicators}
                                            geoDetails={geoDetails}
                                            geoTag={geoTag}
                                            selectionActive={selectionActive}
                                        />
                                    ))}

                                    {/* Race & Ethnicity block */}
                                    {race && (
                                        <div className="col-span-2 w-full mt-2">
                                            <RaceEthnicityBarChartCard
                                                raceMetric={race}
                                                ethnicityMetric={ethnicity}
                                                disaggData={indicators}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                                height="h-52"
                                                background="bg-white"
                                                labelOverride={140}
                                            />
                                        </div>
                                    )}

                                    {/* Disaggregated Charts block */}
                                    {commute && (
                                        <div className="col-span-2 w-full mt-2">
                                            <MetricBarChart
                                                metric={commute}
                                                disaggData={indicators}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive
                                                height="h-60"
                                                background="bg-white"
                                            />
                                        </div>
                                    )}
                                    {annualHHIncome && (
                                        <div className="col-span-2 w-full mt-2">
                                            <MetricBarChart
                                                metric={annualHHIncome}
                                                disaggData={indicators}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive
                                                height="h-60"
                                                background="bg-white"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </DashboardColorContext.Provider>
                    )}
                </div>
            </div>
        );
    };

    // Set active tab to current tab if present
    const activeTab = currentTab?.toLowerCase() || tabs[0];

    return (
        <div className={`${!isIframe ? 'max-w-[456px] mx-auto print:max-w-full' : ''}`}>
            <div className="flex mr-4 items-center justify-end ">
            </div>
            {/* Functional Icons for Non-iframe View */}
            {!isIframe && (
                <div className="my-2 mx-5 flex items-center justify-end gap-3">
                    {layers.length > 0 && <img src={layerSvg} alt="layer icon" title="Show metric layers" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleShowLayers() }}/> }
                    <img src={printSvg} alt="print icon" title="Print PDF" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handlePrintNoIframe() }}/>
                    <img src={downloadSvg} alt="download icon" title="Download Excel" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleDownloadNoIframe() }}/>
                    <img src={mapSvg} alt="map icon" title="Show Mappable Metrics" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handleMappableModal() }}/>
                </div>
            )}

            {/* Tab menu for dashboard categories in non-iframe view */}
            {!isIframe && (
                <div className="sticky top-0 z-10 bg-white">
                    {tabs.length > 0 
                    ? <TabMenu
                        tabs={tabs}
                        activeTab={activeTab}
                        setCurrentTab={setCurrentTab}
                        navigate={navigate}
                    />
                    : <TabMenuSkeleton/>
                    }
                </div>
            )}

            {/* {locationId === 'federalway' &&
                <div className="flex items-end justify-center py-2 px-4 bg-[#21506F]">
                    <button
                        onClick={() => navigate("/parcels")}
                        className="rounded-md bg-[#88b637] px-3 py-2 text-[10.8px] font-bold text-white shadow-sm hover:bg-zinc-800"
                        >
                            View Parcel Data
                    </button>
                </div>
            } */}

            {/* Render current dashboard view */}
            <div className="no-print">
                {!isModalOpen && !isProgrammaticModalOpen && (
                    isLoading ? (
                        isMetricTab === undefined ? null :
                        isMetricTab ? (
                            <div className="mx-auto px-4 pb-8 pt-4">
                                <div className="grid grid-cols-2 gap-4 w-full">
                                {[...Array(6)].map((_, i) => (
                                    <MetricCardSkeleton key={i} />
                                ))}
                                </div>
                            </div>
                        ) : (
                            <div className="mx-4">
                                <TableSkeleton />
                            </div>
                        )
                    ) : (
                        renderMetrics(currentTab)
                    )
                )}
            </div>


            {/* #region: Modals */}
            <LegendMetricModal
                id={legendId}
                isOpen={isLegendModalOpen}
                onClose={closeLegendModal}
                geoDetails={geoDetails}
                svg={getSvgMap(legendId)}
                geoTag={geoTag}
                selectionActive={selectionActive}
            />

            {isModalOpen && <MetricsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                geoDetails={geoDetails}
                geoTag={geoTag}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                legendUrl={legendUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
            />}

            {isMappableModalOpen && <MappableMetricsModal
                isOpen={isMappableModalOpen}
                onClose={closeMappableModal}
            />}

            {isLayersModalOpen && <LayersModal isOpen={isLayersModalOpen} onClose={handleShowLayers} />}

            {mapItemDetailModalOpen && <MapItemDetailsModal layerId={mapItemParams?.layerId} pointId={mapItemParams?.id} isOpen={mapItemDetailModalOpen} onClose={closeMapItemModal}/> }

            {isProgrammaticModalOpen && (
                <ProgrammaticModal
                isOpen={isProgrammaticModalOpen}
                onClose={closeProgrammaticModal}
                layers={layers}
                geoDetails={geoDetails}
                geoTag={geoTag}
                pdfUrl={pdfUrl}
                logoUrl={logoUrl}
                selectedArea={selectedArea}
                siteUrl={siteUrl}
                />
            )}

            {showDownloadLoading &&
                <DownloadLoading/>
            }
        </div>
    );
}

export default Dashboard;